import { StateService } from '@uirouter/angularjs';
import * as ng from 'angular';
import { ActionRegistry, ActionServerColumnDef, GridConfigDto, ServerActionDef } from './adapter/grid-config-adapter.types';

export interface MassiaActionMeta extends ServerActionDef {
    // Les propriétés sont déjà dans ServerActionDef suite à l'update
}

export class MassiaActionDispatcher {
    constructor(
        private $state: StateService,
        private $http: ng.IHttpService,
        private notification: { success: (msg: string) => void; error: (msg: string) => void },
        private ModalService: {
            confirm: (opts: unknown) => { result: Promise<void> };
            open: (opts: unknown) => { result: Promise<unknown> };
        } & Record<string, unknown>,
        private $translate: ng.translate.ITranslateService,
        private DataShareService: { setDataShare: (data: unknown, key: string) => void }
    ) {}

    /**
     * Construit automatiquement un actionRegistry partiel
     * à partir des actions déclarées dans la config serveur.
     * Les actions "custom" ne sont PAS dans ce registry — elles
     * restent à la charge du controller métier.
     *
     * @param serverConfig       Config brute retournée par le back
     * @param onAfterHttpAction  Callback pour recharger la grille après une action HTTP/modal
     * @param getCurrentContext  Retourne les données courantes du searchForm (pour saveDataShare)
     */
    buildRegistryFromConfig(
        serverConfig: GridConfigDto,
        onAfterHttpAction?: () => void,
        getCurrentContext?: () => unknown,
        fallbackDataShareKey?: string
    ): ActionRegistry {
        const registry: ActionRegistry = {};
        const dataShareKey = serverConfig.features?.dataShare?.key || fallbackDataShareKey;

        const actionCols = (serverConfig.columns || []).filter((c) => c.columnType === 'action') as ActionServerColumnDef[];
        for (const col of actionCols) {
            for (const action of col.actions || []) {
                const meta = action as MassiaActionMeta;

                //navigate (par exemple click sur une ligne du tableau)
                if (meta.actionType === 'navigate') {
                    registry[action.key] = (rowData: Record<string, unknown>) => {
                        // Sauvegarder le contexte avant navigation si demandé
                        if (meta.saveDataShare && dataShareKey && getCurrentContext) {
                            this.DataShareService.setDataShare(getCurrentContext(), dataShareKey);
                        }
                        if (meta.href) {
                            this.$state.go(meta.href, { id: rowData.id });
                        }
                    };
                }

                //http (par exemple un bouton de suppression, qui necessite une modal de confiramtion)
                else if (meta.actionType === 'http') {
                    registry[action.key] = async (rowData: Record<string, unknown>) => {
                        if (meta.modalMessageKey) {
                            // type de modal (confirm, ...)
                            const modalType = meta.modalType
                                ? (this.ModalService[meta.modalType] as (opts: unknown) => { result: Promise<void> })
                                : this.ModalService.confirm;

                            const modalInstance = modalType.call(this.ModalService, {
                                modalTitle: meta.modalTitleKey ? this.$translate.instant(meta.modalTitleKey, rowData) : undefined,
                                modalMsg: this.$translate.instant(meta.modalMessageKey, rowData),
                                headerClass: meta.modalHeaderClass
                            });
                            try {
                                await modalInstance.result;
                            } catch {
                                // modal dismissed → annuler
                                return;
                            }
                        }

                        const method = (meta.httpMethod || 'POST').toLowerCase();
                        const url = `${__configuration.apiUrl}${meta.url!.replace('{id}', String(rowData.id || ''))}`;

                        try {
                            if (method === 'delete') {
                                await this.$http.delete(url);
                            } else if (method === 'get') {
                                await this.$http.get(url);
                            } else {
                                await (this.$http as unknown as Record<string, (url: string, body: unknown) => Promise<void>>)[method](url, {});
                            }

                            if (meta.notificationSuccessKey) {
                                this.notification.success(this.$translate.instant(meta.notificationSuccessKey, rowData));
                            }

                            if (onAfterHttpAction) {
                                onAfterHttpAction();
                            }
                        } catch (err) {
                            console.error('[MassiaActionDispatcher] HTTP Error:', err);
                            if (meta.notificationErrorKey) {
                                this.notification.error(this.$translate.instant(meta.notificationErrorKey, rowData));
                            }
                        }
                    };
                }

                //modal (utilisation d'un composant modal)
                else if (meta.actionType === 'modal') {
                    registry[action.key] = (rowData: Record<string, unknown>) => {
                        // Construire les resolves à partir des déclarations
                        const modalResolve: Record<string, () => unknown> = {};
                        const allModalResolveValues: Record<string, unknown> = {};

                        // Valeurs statiques ( ce sont principalement les element ne faisant pas partie des propriété de l'objet rowData)
                        for (const [key, val] of Object.entries(meta.modalResolveStatic || {})) {
                            allModalResolveValues[key] = val;
                        }

                        // Valeurs du contexte global (context du formulaire du tableau)
                        const currentContext = getCurrentContext ? getCurrentContext() : {};

                        // Valeurs de la ligne (rowData) ou du contexte
                        for (const [resolveKey, rowField] of Object.entries(meta.modalResolveFromRow || {})) {
                            if (rowField.startsWith('$context.')) {
                                const contextField = rowField.replace('$context.', '');
                                allModalResolveValues[resolveKey] = (currentContext as Record<string, unknown>)?.[contextField];
                            } else {
                                // Mappage intelligent : on cherche le champ exact ou une version "numero" -> "code"
                                allModalResolveValues[resolveKey] = (rowData as Record<string, unknown>)[rowField];
                            }
                        }

                        // Mapper allModalResolveValues dans le format modalResolve de $uibModal
                        for (const [key, val] of Object.entries(allModalResolveValues)) {
                            modalResolve[key] = () => val;
                        }

                        // Style "FactureCloturePopup" : injecter l'objet complet sous la clé 'modalResolve'
                        modalResolve['resolve'] = () => allModalResolveValues;

                        const modalInstance = this.ModalService.open({
                            component: meta.modalComponent,
                            size: meta.modalSize || 'lg',
                            resolve: modalResolve
                        });

                        modalInstance.result
                            .then(async (result: unknown) => {
                                if (!result) return;

                                if (meta.modalUrl) {
                                    // const url = meta.modalUrl.replace('{id}', rowData.id);
                                    const url = `${__configuration.apiUrl}${meta.modalUrl.replace('{id}', String(rowData.id || ''))}`;
                                    const method = (meta.modalMethod || 'POST').toLowerCase();

                                    try {
                                        // Si le result est un objet, on peut injecter des éléments du contexte
                                        const payload = typeof result === 'object' && result !== null ? { ...result } : result;

                                        // Injection automatique de societeComIdSelected si on est dans le contexte factures
                                        if (currentContext && (currentContext as Record<string, unknown>).societeComIdSelected) {
                                            if (typeof payload === 'object' && payload !== null) {
                                                (payload as Record<string, unknown>).idSocieteCom = (
                                                    currentContext as Record<string, unknown>
                                                ).societeComIdSelected;
                                            }
                                        }

                                        const response = await (
                                            this.$http as unknown as Record<
                                                string,
                                                (url: string, body: unknown) => Promise<ng.IHttpResponse<{ data: string }>>
                                            >
                                        )[method](url, payload);

                                        if (meta.modalSuccessKey) {
                                            this.notification.success(this.$translate.instant(meta.modalSuccessKey, rowData));
                                        }

                                        // Navigation optionnelle vers l'entité créée/modifiée
                                        if (meta.modalNavigateTo && response?.data) {
                                            this.$state.go(meta.modalNavigateTo, { id: response.data });
                                        }

                                        if (onAfterHttpAction) {
                                            onAfterHttpAction();
                                        }
                                    } catch (err) {
                                        console.error('[MassiaActionDispatcher] Modal action error:', err);
                                        if (meta.modalErrorKey) {
                                            this.notification.error(this.$translate.instant(meta.modalErrorKey, rowData));
                                        }
                                    }
                                }
                            })
                            .catch(() => {
                                // Modal dismissed — no-op
                            });
                    };
                }
            }
        }
        return registry;
    }
}

MassiaActionDispatcher.$inject = ['$state', '$http', 'notification', 'ModalService', '$translate', 'DataShareService'];
